import { FC, useEffect, useMemo, useState } from 'react';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import TableTag from '@components/table_tag';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';
import {
    ETransactionAction,
    ETransactionType,
    TTransactionsFilter
} from '@xeppt/xeppt-sdk/types/transaction';
import {
    transactionsActionsFilterArray,
    transactionsMethodsFilterArray
} from '@const/transactions';
import { getDisableFeature } from '@utils/index';
import RangeDatePicker from '@components/range_date_picker';
import { ETransactionMethod } from '@xeppt/xeppt-sdk/types';

interface IProps {
    toggleFilter: () => void;
    onChangeFilter: (val: TTransactionsFilter) => void;
    activeTab: string;
    triggerClearFilter: number;
}

const FilterSection: FC<IProps> = ({
    toggleFilter,
    onChangeFilter,
    activeTab,
    triggerClearFilter
}) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'transactions.filter'
    });
    const { labelLocale } = useLocales();
    const [dateFilter, setDateFilter] = useState('');
    const [date_from, setDateFrom] = useState<string | null>(null);
    const [date_to, setDateTo] = useState<string | null>(null);
    const [typeFilter, setTypeFilter] = useState<ETransactionAction[]>([]);
    const [method, setMethod] = useState<ETransactionMethod[]>([]);
    const [activeDirection, setActionDirection] = useState<ETransactionType[]>([]);

    const handleChangeType = (value: ETransactionAction) => {
        if (typeFilter.includes(value)) {
            setTypeFilter((state) => state.filter((item) => item !== value));
        } else {
            setTypeFilter((state) => [...state, value]);
        }
    };

    const handleChangeMethod = (value: ETransactionMethod) => {
        if (method.includes(value)) {
            setMethod((state) => state.filter((item) => item !== value));
        } else {
            setMethod((state) => [...state, value]);
        }
    };

    const handleChangeDirection = (value: ETransactionType) => {
        if (activeDirection.includes(value)) {
            setActionDirection((state) => state.filter((item) => item !== value));
        } else {
            setActionDirection((state) => [...state, value]);
        }
    };

    useEffect(() => {
        if (triggerClearFilter > 0) {
            setTypeFilter([]);
            setMethod([]);
            setActionDirection([]);
            setDateFilter('');
            setDateFrom(null);
            setDateTo(null);
        }
    }, [triggerClearFilter]);

    useEffect(() => {
        if (date_to && date_from) {
            onChangeFilter({
                action: typeFilter,
                date_to: new Date(date_to),
                date_from: new Date(date_from),
                method,
                type: activeDirection
            });
        } else if (typeFilter) {
            onChangeFilter({
                action: typeFilter,
                method,
                type: activeDirection
            });
        }
    }, [dateFilter, typeFilter, activeDirection, date_from, date_to, method]);

    const transactionsActionsFilter = useMemo(() => {
        return transactionsActionsFilterArray(t);
    }, []);

    return (
        <div className={styles.wrapper}>
            <div className={styles.title_wrapper}>
                <Typography variant="h4">Filters</Typography>
                <button onClick={toggleFilter}>
                    <Icon name="close" />
                </button>
            </div>
            <div className={styles.block_wrapper}>
                <RangeDatePicker
                    values={[date_from, date_to]}
                    onChange={(val) => {
                        setDateFrom(val?.[0]);
                        setDateTo(val?.[1]);
                        setDateFilter('');
                    }}
                    maxDate={getDisableFeature()}
                    isDisableFeature
                    full
                    label={labelLocale('date_range')}
                />
            </div>
            {activeTab === 'transactions' && (
                <>
                    <div className={styles.block_wrapper}>
                        <Typography variant="body3" weight="semibold">
                            {t('type')}
                        </Typography>
                        <div className={styles.tags_wrapper}>
                            <TableTag
                                isActive={activeDirection.includes(ETransactionType.DEBIT)}
                                onClick={() => handleChangeDirection(ETransactionType.DEBIT)}>
                                Incoming
                            </TableTag>
                            <TableTag
                                isActive={activeDirection.includes(ETransactionType.CREDIT)}
                                onClick={() => handleChangeDirection(ETransactionType.CREDIT)}>
                                Outgoing
                            </TableTag>
                        </div>
                    </div>
                    <div className={styles.block_wrapper}>
                        <Typography variant="body3" weight="semibold">
                            Method
                        </Typography>
                        <div className={styles.tags_wrapper}>
                            {transactionsMethodsFilterArray(t).map((item) => {
                                return (
                                    <TableTag
                                        key={item.method}
                                        isActive={method.includes(item.method)}
                                        onClick={() => handleChangeMethod(item.method)}>
                                        {item.label}
                                    </TableTag>
                                );
                            })}
                        </div>
                    </div>
                    <div className={styles.block_wrapper}>
                        <Typography variant="body3" weight="semibold">
                            Action
                        </Typography>
                        <div className={styles.tags_wrapper}>
                            {transactionsActionsFilter.map((item) => {
                                return (
                                    <TableTag
                                        key={item.action}
                                        isActive={typeFilter.includes(item.action)}
                                        onClick={() => handleChangeType(item.action)}>
                                        {item.label}
                                    </TableTag>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default FilterSection;
